import { useNavigate } from 'react-router-dom';
import Topbar from '../components/Topbar';
import DarkCard from '../components/DarkCard';
import DarkButton from '../components/DarkButton';
import ThemeRoot from '../components/ThemeRoot';
import { StagePath, ChevronRight } from '../svg/icons';
import { haptic } from '../lib/haptic';
import { useDarkStore } from '../store/darkStore';

// Winding path coordinates (left-right zigzag) inside a 320 × 560 SVG box.
const PATH_XY = [
  { x: 160, y: 60 },
  { x: 220, y: 150 },
  { x: 100, y: 240 },
  { x: 220, y: 330 },
  { x: 100, y: 420 },
  { x: 160, y: 510 },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { stages, explorerName, avatarEmoji } = useDarkStore();

  const activeStage = stages.find((s) => s.status === 'active');
  const allDone = stages.every((s) => s.status === 'done');

  const points = stages.map((s, i) => ({
    x: PATH_XY[i].x,
    y: PATH_XY[i].y,
    status: s.status,
  }));

  const handleNodeClick = (idx: number, status: 'done' | 'active' | 'locked') => {
    if (status === 'locked') {
      haptic('wrong');
      return;
    }
    haptic('tap');
    const stage = stages[idx];
    if (status === 'active') {
      navigate(`/home/challenge/${stage.id}`);
    } else if (status === 'done') {
      navigate(`/home/review/${stage.id}`);
    }
  };

  return (
    <ThemeRoot>
      <Topbar />

      <main className="mx-auto grid max-w-5xl gap-6 px-4 py-6 md:grid-cols-[1fr_320px] md:gap-10 md:px-8 md:py-10">
        <section>
          <div className="mb-3 font-marker text-xl text-amber/90">
            Hey {explorerName || 'founder'} {avatarEmoji}
          </div>
          <h1 className="font-marker text-3xl text-gray-900 dark:text-ink md:text-4xl">
            Your Launchpad
          </h1>
          <p className="mt-1 font-gill text-gray-600 dark:text-ink/70">
            {allDone
              ? 'All six stages cleared — your business plan is ready. 🎉'
              : 'Tap the glowing stage to keep building.'}
          </p>

          <div className="mt-6 flex justify-center">
            <StagePath points={points} onNodeClick={handleNodeClick} />
          </div>
        </section>

        <aside className="space-y-5">
          {allDone ? (
            <DarkCard active>
              <div className="font-marker text-[11px] uppercase tracking-[0.2em] text-mint-dim dark:text-mint">
                All stages complete
              </div>
              <div className="mt-1 font-marker text-2xl text-gray-900 dark:text-ink">
                Your plan is ready 🦊
              </div>
              <p className="mt-2 font-gill text-sm text-gray-700 dark:text-ink/80">
                KoKo has written up your full business plan. Read it, download it, or share it on Spotlight.
              </p>
              <DarkButton
                accent="amber"
                hapticKind="levelUp"
                className="mt-5 w-full"
                onClick={() => navigate('/home/summary')}
              >
                See your Business Plan <ChevronRight size={18} />
              </DarkButton>
            </DarkCard>
          ) : activeStage ? (
            <DarkCard active>
              <div className="font-marker text-[11px] uppercase tracking-[0.2em] text-amber">
                Up next · Stage {activeStage.id}
              </div>
              <div className="mt-1 flex items-center gap-2 font-marker text-2xl text-gray-900 dark:text-ink">
                <span>{activeStage.emoji}</span>
                <span>{activeStage.name}</span>
              </div>
              <p className="mt-2 font-gill text-sm text-gray-700 dark:text-ink/80">
                {activeStage.questions.length} quick questions · earn XP as you go.
              </p>
              <DarkButton
                accent="amber"
                className="mt-5 w-full"
                onClick={() => navigate(`/home/challenge/${activeStage.id}`)}
              >
                Start challenge <ChevronRight size={18} />
              </DarkButton>
            </DarkCard>
          ) : null}

          <DarkCard>
            <div className="font-marker text-[11px] uppercase tracking-[0.2em] text-mint-dim dark:text-mint">
              Stages
            </div>
            <ul className="mt-3 space-y-2">
              {stages.map((s) => {
                const clickable = s.status === 'done' || s.status === 'active';
                const href =
                  s.status === 'done'
                    ? `/home/review/${s.id}`
                    : s.status === 'active'
                      ? `/home/challenge/${s.id}`
                      : null;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      disabled={!clickable}
                      onClick={() => {
                        if (!href) {
                          haptic('wrong');
                          return;
                        }
                        haptic('tap');
                        navigate(href);
                      }}
                      className={`flex w-full items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2 text-left transition-colors dark:border-white/5 dark:bg-base/60 ${
                        clickable
                          ? 'hover:border-amber/40 dark:hover:border-amber/40'
                          : 'cursor-not-allowed opacity-80'
                      }`}
                    >
                      <span className="flex items-center gap-2 font-marker text-lg text-gray-900 dark:text-ink">
                        <span>{s.emoji}</span>
                        <span>
                          {s.id}. {s.name}
                        </span>
                      </span>
                      <span
                        className={`font-marker text-xs uppercase tracking-wider ${
                          s.status === 'done'
                            ? 'text-mint-dim dark:text-mint'
                            : s.status === 'active'
                              ? 'text-amber'
                              : 'text-gray-400 dark:text-ink/30'
                        }`}
                      >
                        {s.status}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </DarkCard>

          <DarkButton
            accent="violet"
            full
            onClick={() => navigate('/home/progress')}
          >
            View progress
          </DarkButton>

          <DarkButton
            accent="mint"
            full
            onClick={() => navigate('/home/plans')}
          >
            My Plans
          </DarkButton>
        </aside>
      </main>
    </ThemeRoot>
  );
}
